import {
  BulkDoctorInvitationRequest,
  BulkRowError,
  DoctorInvitationDto,
  DoctorInvitationRequest,
} from "@/types/doctorInvitation.definition";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import doctorInvitationApi from "../../services/doctorInvitation.api";
import {
  InvitationApiError,
  bulkInviteDoctorsFailure,
  bulkInviteDoctorsRequest,
  bulkInviteDoctorsSuccess,
  cancelInvitationFailure,
  cancelInvitationRequest,
  cancelInvitationSuccess,
  fetchInvitationsFailure,
  fetchInvitationsRequest,
  fetchInvitationsSuccess,
  inviteDoctorFailure,
  inviteDoctorRequest,
  inviteDoctorSuccess,
  resendInvitationFailure,
  resendInvitationRequest,
  resendInvitationSuccess,
} from "../reducers/doctorInvitationSlice";

type ApiErrorShape = {
  code?: string;
  message?: string;
  data?: BulkRowError[];
};

function toInvitationApiError(
  error: unknown,
  fallbackMessage: string
): InvitationApiError {
  const e = error as ApiErrorShape;
  if (e && typeof e === "object" && e.code) {
    return { code: e.code, message: e.message, data: e.data };
  }
  return { code: "UNHANDLED_ERROR", message: fallbackMessage };
}

// Sagas
function* fetchInvitationsSaga() {
  try {
    const invitations: DoctorInvitationDto[] = yield call(
      doctorInvitationApi.getAllInvitations
    );
    yield put(fetchInvitationsSuccess(invitations));
  } catch (error) {
    yield put(
      fetchInvitationsFailure(
        toInvitationApiError(error, "Failed to fetch invitations")
      )
    );
  }
}

function* inviteDoctorSaga(action: { payload: DoctorInvitationRequest }) {
  try {
    const invitation: DoctorInvitationDto = yield call(
      doctorInvitationApi.inviteDoctor,
      action.payload
    );
    yield put(inviteDoctorSuccess(invitation));
  } catch (error) {
    yield put(
      inviteDoctorFailure(
        toInvitationApiError(error, "Failed to send invitation")
      )
    );
  }
}

function* resendInvitationSaga(action: { payload: number }) {
  try {
    const invitation: DoctorInvitationDto = yield call(
      doctorInvitationApi.resendInvitation,
      action.payload
    );
    yield put(resendInvitationSuccess(invitation));
  } catch (error) {
    yield put(
      resendInvitationFailure(
        toInvitationApiError(error, "Failed to resend invitation")
      )
    );
  }
}

function* cancelInvitationSaga(action: { payload: number }) {
  try {
    yield call(doctorInvitationApi.cancelInvitation, action.payload);
    yield put(cancelInvitationSuccess(action.payload));
  } catch (error) {
    yield put(
      cancelInvitationFailure(
        toInvitationApiError(error, "Failed to cancel invitation")
      )
    );
  }
}

function* bulkInviteDoctorsSaga(action: {
  payload: BulkDoctorInvitationRequest;
}) {
  try {
    const invitations: DoctorInvitationDto[] = yield call(
      doctorInvitationApi.inviteDoctorsBulk,
      action.payload
    );
    yield put(bulkInviteDoctorsSuccess(invitations));
  } catch (error) {
    yield put(
      bulkInviteDoctorsFailure(
        toInvitationApiError(error, "Failed to send bulk invitations")
      )
    );
  }
}

// Root saga
export function* doctorInvitationSaga() {
  yield takeLatest(fetchInvitationsRequest, fetchInvitationsSaga);
  yield takeEvery(inviteDoctorRequest, inviteDoctorSaga);
  yield takeEvery(resendInvitationRequest, resendInvitationSaga);
  yield takeEvery(cancelInvitationRequest, cancelInvitationSaga);
  yield takeEvery(bulkInviteDoctorsRequest, bulkInviteDoctorsSaga);
}
